import { ReactNode, useCallback, useEffect, useState } from "react";
import { unwrapResult } from "@reduxjs/toolkit";
import {
    Box,
    Paper,
    Tab,
    Stack,
    Typography,
    Divider,
    Chip,
    Alert,
    Tooltip,
    FormGroup,
    FormControlLabel,
    Checkbox,
} from "@mui/material";
import { Localized, useLocalization } from "@fluent/react";
import Grid from '@mui/material/Grid'
import Avatar from '@mui/material/Avatar'
import LanguageIcon from '@mui/icons-material/Language'
import PlaceIcon from '@mui/icons-material/Place'
import BusinessIcon from '@mui/icons-material/Business'

import produce from "immer";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { CompanyProfile, loadCompanyProfile, updateCompanyLogo } from "./companyProfileApi";
import { LoadingContainer } from "../../../app/LoadingContainer";
import { ErrorContainer } from "../../../app/ErrorContainer";
import EditIcon from '@mui/icons-material/Edit'
import FactCheckIcon from '@mui/icons-material/FactCheck'

import { Button } from '@mui/material'
import { CompanyProfileEditWizard } from "./CompanyProfileEditWizard";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import ImageIcon from "@mui/icons-material/Image";
import { LinearProgress } from "@mui/material";
import { selectBearerToken } from "../../user/userSlice";
import PublicIcon from '@mui/icons-material/Public';
import { MARKET_REGIONS_BY_NAME, MarketRegionName, toRegionName } from "./marketRegion";
import VerifiedIcon from '@mui/icons-material/Verified';
import { CERTIFICATION_LABEL, CertificationStandardEnum } from "./companyCertificates";
import { CompanyConsentTypeEnum } from "./enums/companyConsentEnum";
import { SupplierDevicesList } from "../../supplierdevices/SupplierDevicesList";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ShareIcon from "@mui/icons-material/Share";
import LinkIcon from "@mui/icons-material/Link";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import TwitterIcon from "@mui/icons-material/Twitter";
import YouTubeIcon from "@mui/icons-material/YouTube";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import RedditIcon from "@mui/icons-material/Reddit";
import { SocialPlatformEnum } from "./enums/socialPlatformEnum";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import MusicNoteIcon from "@mui/icons-material/MusicNote"; // TikTok
import { QUICK_TAG_LABEL } from "./enums/QuickTagEnum";
import { EditIconButton } from "../../../app/components/iconButtons/EditIconButton";
import React from "react";
import { KeyValueRow, FieldKey } from "./controls/KeyValueRow";
import { EditCompanyProfileField } from "./dialogs/EditCompanyProfileField";

export const COMPANY_SIZE_TIER_LABEL: Record<string, string> = {
    'Employees_1_9': '1–9',
    'Employees_10_49': '10–49',
    'Employees_50_99': '50–99',
    'Employees_100_249': '100–249',
    'Employees_250_499': '250–499',
    'Employees_500_999': '500–999',
    'Employees_1000_4999': '1000–4999',
    'Employees_5000_9999': '5000–9999',
    'Employees_10000_24999': '10000–24999',
    'Employees_25000_49999': '25000–49999',
    'Employees_50000_Plus': '50000+',
};

const SOCIAL_COLOR: Record<number, string> = {
    [SocialPlatformEnum.LinkedIn]: "#0A66C2",
    [SocialPlatformEnum.X]: "#000000",
    [SocialPlatformEnum.YouTube]: "#FF0000",
    [SocialPlatformEnum.Facebook]: "#1877F2",
    [SocialPlatformEnum.Instagram]: "#E1306C",
    [SocialPlatformEnum.Reddit]: "#FF4500",
};

export const tierLabel = (val: string | undefined) => {
    if (val) {
        return COMPANY_SIZE_TIER_LABEL[val] ?? '—';
    };
}

function getPlatformIcon(p: SocialPlatformEnum) {
    switch (p) {
        case SocialPlatformEnum.LinkedIn: return <LinkedInIcon fontSize="small" />;
        case SocialPlatformEnum.X: return <TwitterIcon fontSize="small" />;
        case SocialPlatformEnum.YouTube: return <YouTubeIcon fontSize="small" />;
        case SocialPlatformEnum.Facebook: return <FacebookIcon fontSize="small" />;
        case SocialPlatformEnum.Instagram: return <InstagramIcon fontSize="small" />;
        case SocialPlatformEnum.Reddit: return <RedditIcon fontSize="small" />;
        default: return <LinkIcon fontSize="small" />;
    }
}

const safeHost = (url?: string | null) =>
    !url ? '' : url.replace(/^https?:\/\//i, '').replace(/\/$/, '');
const dash = (v?: string | null) => (v && v.trim().length ? v : '—');

export const CompanyProfileForm = () => {
    const dispatch = useAppDispatch();

    const [data, setData] = useState<CompanyProfile | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<any>(null);
    const [isError, setIsError] = useState<boolean>(false)


    const handleUpdated = useCallback((updated: CompanyProfile) => {
        setData(prev =>
            prev
                ? produce(prev, draft => {
                    Object.assign(draft as any, updated);
                })
                : updated
        );
    }, []);

    useEffect(() => {
        setIsLoading(true)
        dispatch(loadCompanyProfile())
            .then(unwrapResult)
            .then((profile: CompanyProfile) => {
                setData(profile);
            })
            .catch((error) => { console.error(error); setIsError(true); setError(error); })
            .finally(() => setIsLoading(false))
    }, [])

    if (isLoading) {
        return <LoadingContainer />
    }
    if (isError) {
        return <ErrorContainer error={error} />
    }
    if (!data) {
        return <>No data</>;
    }

    return <>
        <CompanyProfileFormInternal system={data} onUpdated={handleUpdated} />
    </>;
};


export const CompanyProfileFormInternal = ({
    system,
    onUpdated
}: {
    system: CompanyProfile
    onUpdated: (updated: CompanyProfile) => void
}) => {
    const dispatch = useAppDispatch()
    // ===== Helpers (widok) =====
    const initials = (system.shortName || system.legalName || '?')
        .split(' ')
        .filter(Boolean)
        .map(w => w[0]!.toUpperCase())
        .slice(0, 2)
        .join('');


    const marketActiveView = (system as any).isMarketPlaceActive ?? (system as any).IsMarketPlaceActive ?? false;
    const restrictVisibilityToBuyers = (system as any).restrictVisibilityToBuyers ?? false;
    const [wizardOpen, setWizardOpen] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState<number | null>(null);
    const [logo, setLogo] = useState<string | null>(system.logoUrl)
    const authHeader = useAppSelector(selectBearerToken)
    const { l10n } = useLocalization();

    const [editField, setEditField] = React.useState<FieldKey | null>(null);
    const [editValue, setEditValue] = React.useState<ReactNode>("");

    const openEdit = (field: FieldKey, value?: ReactNode | null) => {
        setEditField(field);
        setEditValue(value ?? "");
    };

    const closeEdit = () => setEditField(null);

    type FormTabs = "base" | "marketplace" | "social" | "logo" | "markets" | "certificates";

    const [tab, setTab] = useState<FormTabs>("base");
    const handleTabChange = useCallback((_, newTab) => {
        setTab(newTab)
    }, [])

    const onLogoInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        if (f) await handleLogoUpload(f);
        e.target.value = "";
    };

    const validateLogo = (file: File): string | null => {
        const allowed = ["image/png", "image/jpeg", "image/webp", "image/svg+xml"];
        const maxBytes = 2 * 1024 * 1024;
        if (!allowed.includes(file.type)) return "Dopuszczalne formaty: PNG, JPG, WEBP, SVG.";
        if (file.size > maxBytes) return "Maksymalny rozmiar pliku to 2 MB.";
        return null;
    };

    type UploadLogoResult = { fileId: string; url?: string };

    const handleLogoUpload = async (file: File) => {
        const err = validateLogo(file);
        if (err) {
            setUploadError(err);
            return;
        }

        setUploading(true);
        setUploadError(null);
        try {
            // 1) Upload the bytes to the dedicated company-logo endpoint.
            const fd = new FormData();
            fd.append("file", file);

            const response = await fetch(
                `${process.env.REACT_APP_REST_API}api/company-profile/company-logo`,
                {
                    method: "POST",
                    mode: "cors",
                    headers: {
                        ...authHeader, // UWAGA: bez 'Content-Type' przy FormData
                    },
                    body: fd,
                }
            );

            if (!response.ok) {
                const problem = await response.json().catch(() => null);
                throw new Error(problem?.title ?? "Nie udało się wgrać logo.");
            }

            const upload: UploadLogoResult = await response.json();
            const fileId = upload.fileId;

            // 2) Persist the public (AllowAnonymous) content URL as the company logo.
            // Marketplace cards + this preview render it directly as <img src>; the
            // lastModification query key busts the long-lived response cache per upload.
            const contentUrl = `${process.env.REACT_APP_REST_API}api/company-profile/company-logo/${fileId}/content?lastModification=${Date.now()}`;

            await dispatch(updateCompanyLogo({ id: system.id, url: contentUrl })).unwrap();
            setLogo(contentUrl);
        } catch (e: any) {
            console.error(e);
            setUploadError(e?.message ?? "Nie udało się wgrać logo.");
        } finally {
            setUploading(false);
        }
    };

    const handleRemoveLogo = async () => {
        // opcjonalnie: endpoint do usunięcia powiązania logo
        try {
            // setUploading(true);
            // const payload: CompanyProfile & { id: string } = {
            //     ...system,
            //     logoFileId: null as any,
            //     id: (system as any).id,
            // } as any;
            // const updateRes = await dispatch(updateCompanyProfile(payload)).unwrap();
            // setPreviewUrl(null);
            // onUpdated(updateRes);
            // dispatch(showSuccess("Logo usunięte"));
        } catch (e: any) {
            setUploadError("Nie udało się usunąć logo.");
        } finally {
            setUploading(false);
        }
    };

    const iconFor = (pid: number) => {
        const sx = { color: SOCIAL_COLOR[pid] ?? "text.secondary" };
        switch (pid) {
            case SocialPlatformEnum.LinkedIn: return <LinkedInIcon fontSize="small" sx={sx} />;
            case SocialPlatformEnum.X: return <TwitterIcon fontSize="small" sx={sx} />;
            case SocialPlatformEnum.YouTube: return <YouTubeIcon fontSize="small" sx={sx} />;
            case SocialPlatformEnum.Facebook: return <FacebookIcon fontSize="small" sx={sx} />;
            case SocialPlatformEnum.Instagram: return <InstagramIcon fontSize="small" sx={sx} />;
            case SocialPlatformEnum.Reddit: return <RedditIcon fontSize="small" sx={sx} />;
            case SocialPlatformEnum.TikTok: return <MusicNoteIcon fontSize="small" sx={sx} />;
            default: return <LinkIcon fontSize="small" sx={sx} />;
        }
    };

    // Pełna lista pozycji wg Twojego enuma (w tym TikTok z tym samym ID co Instagram)
    const ROWS: Array<{ id: number; label: string }> = [
        // Global – core B2B
        { id: SocialPlatformEnum.LinkedIn, label: "LinkedIn" },
        { id: SocialPlatformEnum.X, label: "X (Twitter)" },
        { id: SocialPlatformEnum.YouTube, label: "YouTube" },
        { id: SocialPlatformEnum.Facebook, label: "Facebook" },
        { id: SocialPlatformEnum.Instagram, label: "Instagram" },
        { id: SocialPlatformEnum.TikTok, label: "TikTok" },
        { id: SocialPlatformEnum.Reddit, label: "Reddit" },

        // B2B katalogi / review
        { id: SocialPlatformEnum.Crunchbase, label: "Crunchbase" },
        { id: SocialPlatformEnum.Cphi, label: "CPHI Online" },

        // Regionalne B2B
        { id: SocialPlatformEnum.Xing, label: "Xing (DACH)" },
        { id: SocialPlatformEnum.WeChat, label: "WeChat (CN)" },
        { id: SocialPlatformEnum.Zhihu, label: "Zhihu (CN)" },
        { id: SocialPlatformEnum.Line, label: "LINE (JP/TW/TH)" },
        { id: SocialPlatformEnum.KakaoTalk, label: "KakaoTalk (KR)" },
        { id: SocialPlatformEnum.NaverBlog, label: "Naver Blog (KR)" },
    ];

    // Szybki odczyt URL-i z system.socialLinks (bez normalizacji)
    const urlsById = new Map<number, string>();
    for (const l of (system.socialLinks ?? [])) {
        const pid = Number(l?.socialPlatformId);
        const url = (l?.url ?? "").trim();
        if (!Number.isNaN(pid) && url && !urlsById.has(pid)) {
            urlsById.set(pid, url); // pierwszy wygrywa
        }
    }

    return (
        <Paper elevation={0} sx={{ p: 2 }}>
            <Stack spacing={2}>
                {/* Nagłówek */}
                {/* === Header (compact) === */}
                <Paper
                    variant="outlined"
                    sx={(t) => ({
                        p: 2,
                        mb: 2,
                        bgcolor: t.palette.grey[100],        // było grey[50]
                        borderColor: t.palette.grey[300],    // mocniejsza ramka
                        borderRadius: 2,
                    })}
                >
                    <Box display="flex" alignItems="center" gap={2}>
                        <Avatar sx={{ width: 64, height: 64, fontSize: 24 }}>
                            {initials}
                        </Avatar>

                        <Box flex={1} minWidth={0}>
                            {/* Tytuł: shortName > legalName */}
                            <Typography variant="h5" sx={{ lineHeight: 1.2 }} noWrap title={system.shortName || system.legalName || '—'}>
                                {system.shortName || system.legalName || '—'}
                            </Typography>

                            {/* Podtytuł: legalName (tylko jeśli różni się od shortName) */}
                            {system.legalName && system.shortName && system.legalName !== system.shortName && (
                                <Typography variant="body2" color="text.secondary" noWrap title={system.legalName}>
                                    {system.legalName}
                                </Typography>
                            )}

                        </Box>

                        {/* PRZYCISK: Edytuj profil */}
                        <Tooltip title={l10n.getString("companies-edit-profile", null, "_Edit profile")}>
                            <Button
                                variant="contained"
                                startIcon={<EditIcon />}
                                onClick={() => setWizardOpen(true)}
                                sx={{ whiteSpace: 'nowrap' }}
                            >
                                <Localized id="companies-edit-profile">_Edit profile</Localized>
                            </Button>
                        </Tooltip>
                    </Box>
                </Paper>

                <Divider />
                <TabContext value={tab}>
                    <TabList onChange={handleTabChange}>
                        <Tab
                            id="0"
                            value="base"
                            label={<Localized id="companies-base-data">_dane</Localized>} />
                        <Tab
                            id="1"
                            value="logo"
                            label={<Localized id="companies-logo">_dane</Localized>} />
                        <Tab
                            id="2"
                            value="social"
                            label={<Localized id="companies-social-media">_dane</Localized>} />
                        <Tab
                            id="3"
                            value="markets"
                            label={<Localized id="companies-markets">_dane</Localized>} />
                        <Tab
                            id="4"
                            value="certificates"
                            label={<Localized id="companies-certificates">_dane</Localized>} />
                        <Tab
                            id="5"
                            value="marketplace"
                            label={<Localized id="companies-marketplace-settings">_Ustawienia marketplace</Localized>}
                        />
                        <Tab
                            id="6"
                            value="devices"
                            label={<Localized id="companies-marketplace-devices">Urządzenia</Localized>}
                        />
                    </TabList>
                    <TabPanel value={"base"} sx={{ p: 0, pt: 2 }}>
                        <SectionHeader icon={<BusinessIcon fontSize="small" />} title="Basic information" />

                        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                            <KeyValueRow field="legalName" label={<Localized id="companies-name">_Company Name</Localized>} value={system.legalName || "—"} openEdit={openEdit} />
                            <KeyValueRow field="shortName" label={<Localized id="companies-short-name">_Short Name</Localized>} value={system.shortName || "—"} openEdit={openEdit} />
                            <KeyValueRow
                                field="about"
                                label={<Localized id="about">_About</Localized>}
                                value={system.about || "—"}
                                multiline
                                openEdit={(field) => {
                                    setEditField(field);
                                    setEditValue(typeof system.about === "string" ? system.about : "");
                                }}
                            />
                            <KeyValueRow field="yearFounded" label={<Localized id="companies-founded">_Founded</Localized>} value={system.yearFounded ?? "—"} openEdit={openEdit} />
                            <KeyValueRow
                                field="companySizeTierId"
                                label={<Localized id="companies-size">_Company size</Localized>}
                                value={tierLabel(system.companySizeTierId)}
                                openEdit={(field) => {
                                    setEditField(field);
                                    setEditValue(system.companySizeTierId ?? "");
                                }}
                            />
                            <KeyValueRow
                                field="quickTags"
                                label={<Localized id="companies-quick-tags">_Quick tags</Localized>}
                                multiline
                                value={
                                    system.quickTags.length === 0 ? (
                                        "—"
                                    ) : (
                                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                            {system.quickTags.map(({ quickTagId }) => (
                                                <Chip key={quickTagId} label={QUICK_TAG_LABEL[quickTagId]} size="small" />
                                            ))}
                                        </Stack>
                                    )
                                }
                                openEdit={(field) => {
                                    setEditField(field);
                                    setEditValue(system.quickTags.map(q => q.quickTagId));
                                }}
                            />
                        </Paper>
                        {/* === Section: Contact details (new style) === */}
                        <SectionHeader icon={<LanguageIcon fontSize="small" />} title="Contact details" />

                        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                            <KeyValueRow
                                field="websiteUrl"
                                label={<Localized id="website">_Website</Localized>}
                                value={
                                    system.websiteUrl ? (
                                        <a
                                            href={system.websiteUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            style={{ textDecoration: "none" }}
                                        >
                                            {safeHost(system.websiteUrl)}
                                        </a>
                                    ) : "—"
                                }
                                openEdit={(field) => {
                                    setEditField(field);
                                    setEditValue(typeof system.websiteUrl === "string" ? system.websiteUrl : "");
                                }}
                            />

                            <KeyValueRow
                                field="primaryEmail"
                                label={<Localized id="companies-primary-email">_Primary email</Localized>}
                                value={
                                    system.primaryEmail ? (
                                        <a href={`mailto:${system.primaryEmail}`}>{system.primaryEmail}</a>
                                    ) : "—"
                                }
                                openEdit={(field) => {
                                    setEditField(field);
                                    setEditValue(typeof system.primaryEmail === "string" ? system.primaryEmail : "");
                                }}
                            />

                            <KeyValueRow
                                field="salesEmail"
                                label={<Localized id="companies-sales-email">_Sales email</Localized>}
                                value={
                                    system.salesEmail ? (
                                        <a href={`mailto:${system.salesEmail}`}>{system.salesEmail}</a>
                                    ) : "—"
                                }
                                openEdit={(field) => {
                                    setEditField(field);
                                    setEditValue(typeof system.salesEmail === "string" ? system.salesEmail : "");
                                }}
                            />

                            <KeyValueRow
                                field="supportEmail"
                                label={<Localized id="companies-support-email">_Support email</Localized>}
                                value={
                                    system.supportEmail ? (
                                        <a href={`mailto:${system.supportEmail}`}>{system.supportEmail}</a>
                                    ) : "—"
                                }
                                openEdit={(field) => {
                                    setEditField(field);
                                    setEditValue(typeof system.supportEmail === "string" ? system.supportEmail : "");
                                }}
                            />

                            <KeyValueRow
                                field="phone"
                                label={<Localized id="companies-phone">_Phone</Localized>}
                                value={
                                    system.phone ? (
                                        <a href={`tel:${system.phone}`}>{system.phone}</a>
                                    ) : "—"
                                }
                                openEdit={(field) => {
                                    setEditField(field);
                                    setEditValue(typeof system.phone === "string" ? system.phone : "");
                                }}
                            />

                            <KeyValueRow
                                field="whatsApp"
                                label="WhatsApp"
                                value={
                                    system.whatsApp
                                        ? (
                                            <a
                                                href={`https://wa.me/${String(system.whatsApp).replace(/\D/g, "")}`}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                {system.whatsApp}
                                            </a>
                                        )
                                        : "—"
                                }
                                openEdit={(field) => {
                                    setEditField(field);
                                    setEditValue(typeof system.whatsApp === "string" ? system.whatsApp : "");
                                }}
                            />
                        </Paper>
                        {/* === Section: Headquarters (HQ) — new style === */}
                        <SectionHeader icon={<PlaceIcon fontSize="small" />} title="Headquarters (HQ)" />

                        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                            <KeyValueRow field="hQ_Street" label={<Localized id="companies-street">_Street</Localized>} value={system.hQ_Street || "—"} openEdit={openEdit} />
                            <KeyValueRow field="hQ_BuildingNumber" label={<Localized id="companies-building-number">_Building number</Localized>} value={system.hQ_BuildingNumber || "—"} openEdit={openEdit} />
                            <KeyValueRow field="hQ_UnitNumber" label={<Localized id="companies-unit-number">_Unit number</Localized>} value={system.hQ_UnitNumber || "—"} openEdit={openEdit} />
                            <KeyValueRow field="hQ_AddressAdditional" label={<Localized id="companies-address-additional">_Address additional</Localized>} value={system.hQ_AddressAdditional || "—"} openEdit={openEdit} />
                            <KeyValueRow field="hQ_City" label={<Localized id="companies-address-city">_City</Localized>} value={system.hQ_City || "—"} openEdit={openEdit} />
                            <KeyValueRow field="hQ_Region" label={<Localized id="companies-region">_Region</Localized>} value={system.hQ_Region || "—"} openEdit={openEdit} />
                            <KeyValueRow field="hQ_RegionCode" label={<Localized id="companies-region-code">_Region code</Localized>} value={system.hQ_RegionCode || "—"} openEdit={openEdit} />
                            <KeyValueRow field="hQ_PostalCode" label={<Localized id="companies-postal-code">_Postal code</Localized>} value={system.hQ_PostalCode || "—"} openEdit={openEdit} />
                            <KeyValueRow field="hQ_CountryCode" label={<Localized id="companies-country-code">_Country code</Localized>} value={system.hQ_CountryCode || "—"} openEdit={openEdit} />
                            <KeyValueRow field="hQ_CountryName" label={<Localized id="companies-country-name">_Country name</Localized>} value={system.hQ_CountryName || "—"} openEdit={openEdit} />


                            {/* Disabled for now
                                <KeyValueRow
                                field="hQ_Coordinates"
                                label={<Localized id="companies-coordinates">_Coordinates</Localized>}
                                value={
                                    system.hQ_Latitude != null && system.hQ_Longitude != null
                                        ? (
                                            <a
                                                href={`https://www.google.com/maps?q=${encodeURIComponent(`${system.hQ_Latitude},${system.hQ_Longitude}`)}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                style={{ textDecoration: "none" }}
                                            >
                                                {`${system.hQ_Latitude}, ${system.hQ_Longitude}`}
                                            </a>
                                        )
                                        : "—"
                                }
                                openEdit={openEdit}
                            /> */}
                        </Paper>

                    </TabPanel>
                    <TabPanel value={"logo"} sx={{ p: 0, pt: 2 }}>
                        <SectionHeader icon={<ImageIcon fontSize="small" />} title="Logo" />

                        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                            {/* Podgląd + akcje */}
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={3}>
                                    <Box
                                        sx={(t) => ({
                                            width: 144,
                                            height: 144,
                                            border: `1px solid ${t.palette.grey[300]}`,
                                            borderRadius: 2,
                                            bgcolor: t.palette.grey[50],
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            overflow: "hidden",
                                        })}
                                    >
                                        {logo ? (
                                            <img
                                                src={logo}
                                                alt="Logo preview"
                                                style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                                            />
                                        ) : (
                                            <Stack alignItems="center" spacing={1} sx={{ color: "text.secondary" }}>
                                                <ImageIcon />
                                                <Typography variant="caption">Brak logo</Typography>
                                            </Stack>
                                        )}
                                    </Box>
                                </Grid>

                                <Grid item xs={12} sm={9}>
                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                                        <Button
                                            variant="outlined"
                                            component="label"
                                            startIcon={<CloudUploadIcon />}
                                            disabled={uploading}
                                        >
                                            <Localized id="companies-logo-upload">Upload logo</Localized>
                                            <input
                                                type="file"
                                                hidden
                                                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                                                onChange={onLogoInputChange}
                                            />
                                        </Button>
                                    </Stack>

                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                        Formats: PNG, JPG, WEBP, SVG. Maks. 100&nbsp;Kb.
                                    </Typography>

                                    {uploading && (
                                        <Box sx={{ mt: 1 }}>
                                            <LinearProgress variant={progress == null ? "indeterminate" : "determinate"} value={progress ?? undefined} />
                                        </Box>
                                    )}

                                    {uploadError && (
                                        <Alert severity="error" sx={{ mt: 1 }}>
                                            {uploadError}
                                        </Alert>
                                    )}
                                </Grid>
                            </Grid>
                        </Paper>
                    </TabPanel>

                    <TabPanel value={"social"} sx={{ p: 0, pt: 2 }}>
                        <SectionHeader icon={<ShareIcon fontSize="small" />} title="Social links" />
                        <SectionSubHeader title={l10n.getString("companies-selected-platforms", null, "_Selected platforms")} />
                        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                            {ROWS.map(({ id, label }) => {
                                const url = urlsById.get(id);
                                return (
                                    <KeyValueRow
                                        key={id}
                                        field="socialLink" 
                                        icon={iconFor(id)}
                                        label={label}
                                        value={url ? (
                                            <Typography
                                                variant="body2"
                                                component="a"
                                                href={url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{ display: "inline-flex", alignItems: "center", wordBreak: "break-all" }}
                                                title={url}
                                            >
                                                {url}
                                                <OpenInNewIcon fontSize="inherit" sx={{ ml: 0.5 }} />
                                            </Typography>
                                        ) : (
                                            <Typography variant="body2" color="text.disabled">—</Typography>
                                        )}
                                        openEdit={(field, _) => openEdit(field, { platformId: id, platformLabel: label, url } as any)} />
                                )
                            })}
                        </Paper>
                    </TabPanel>


                    <TabPanel value={"markets"} sx={{ p: 0, pt: 2 }}>
                        {/* === Section: Markets (regions) — read-only === */}
                        <SectionHeader icon={<PublicIcon fontSize="small" />} title="Markets" />

                        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                            <KeyValueRow
                                field="regionMarkets"
                                label="Selected regions"
                                multiline
                                value={(() => {
                                    const selectedNames = new Set(
                                        (system.markets ?? [])
                                            .map(m => toRegionName(m.regionId as any))
                                            .filter((x): x is MarketRegionName => !!x)
                                    );

                                    const selected = MARKET_REGIONS_BY_NAME.filter(r => selectedNames.has(r.value));

                                    return selected.length === 0 ? "—" : (
                                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                            {selected.map(({ value, label }) => (
                                                <Chip key={value} label={label} size="small" />
                                            ))}
                                        </Stack>
                                    );
                                })()}
                                openEdit={(field, _) => openEdit(field, system.markets as any)}
                            />
                            <KeyValueRow
                                field="countryMarkets"
                                label="Selected countries"
                                multiline
                                value={
                                    system.countryMarkets.length === 0 ? (
                                        "—"
                                    ) : (
                                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                            {system.countryMarkets
                                                .slice()
                                                .sort((a, b) => a.countryName.localeCompare(b.countryName))
                                                .map(({ countryCode, countryName }) => (
                                                    <Chip key={countryCode} label={countryName} size="small" />
                                                ))}
                                        </Stack>
                                    )
                                }
                                openEdit={(field, _) => openEdit(field, system.countryMarkets as any)}
                            />
                        </Paper>

                    </TabPanel>
                    <TabPanel value={"certificates"} sx={{ p: 0, pt: 2 }}>
                        {/* === Section: Certificates === */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <SectionHeader icon={<VerifiedIcon fontSize="small" />} title="Certificates" />
                            <EditIconButton onClick={() => openEdit("certificates", system.certificates as any)} />
                        </Box>

                        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                            {(() => {
                                // helper: zamień string/numer na ID enuma (na wypadek JSON string enums)
                                const toId = (v: number | string): number =>
                                    typeof v === 'number' ? v : (CertificationStandardEnum as any)[v];

                                const selectedIds = new Set(
                                    (system.certificates ?? [])
                                        .map(c => toId(c.certificate as any))
                                        .filter((x): x is number => typeof x === 'number')
                                );

                                // lista wszystkich wartości enuma (numery)
                                const allIds = (Object.values(CertificationStandardEnum) as Array<string | number>)
                                    .filter((v): v is number => typeof v === 'number');

                                return (
                                    <FormGroup>
                                        {allIds.map(id => (
                                            <FormControlLabel
                                                key={id}
                                                control={<Checkbox checked={selectedIds.has(id)} disabled />}
                                                label={CERTIFICATION_LABEL[id as CertificationStandardEnum]}
                                            />
                                        ))}
                                    </FormGroup>
                                );
                            })()}
                        </Paper>


                    </TabPanel>
                    <TabPanel value={"marketplace"} sx={{ p: 0, pt: 2 }}>
                        {/* === Sekcja 1: Marketplace === */}

                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <SectionHeader icon={<FactCheckIcon fontSize="small" />} title="Marketplace" />
                            <EditIconButton onClick={() => openEdit("marketplace", system.consents as any)} />
                        </Box>
                        
                        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                            <Grid container spacing={1.5} alignItems="stretch">
                                {/* lewa 50% */}
                                <Grid item xs={12} md={6}>
                                    <Box
                                        sx={(t) => ({
                                            bgcolor: t.palette.grey[100],
                                            border: `1px solid ${t.palette.grey[200]}`,
                                            borderRadius: 1,
                                            px: 1.25,
                                            py: 1.25,
                                            minHeight: 72,
                                            display: "flex",
                                            alignItems: "center",
                                        })}
                                    >
                                        <Typography variant="body2" color="text.secondary">
                                            Marketplace status
                                        </Typography>
                                    </Box>
                                </Grid>

                                {/* prawa 50% */}
                                <Grid item xs={12} md={6}>
                                    <Box
                                        sx={(t) => ({
                                            border: `1px solid ${t.palette.grey[200]}`,
                                            bgcolor: t.palette.grey[50],
                                            borderRadius: 1,
                                            px: 1.5,
                                            py: 1.25,
                                            minHeight: 72,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "flex-start",
                                            gap: 1,
                                        })}
                                    >
                                        <Chip
                                            label={marketActiveView ? "ACTIVE" : "DISABLED"}
                                            color={marketActiveView ? "success" : "default"}
                                            variant={marketActiveView ? "filled" : "outlined"}
                                            size="small"
                                        />
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Box
                                        sx={(t) => ({
                                            bgcolor: t.palette.grey[100],
                                            border: `1px solid ${t.palette.grey[200]}`,
                                            borderRadius: 1,
                                            px: 1.25,
                                            py: 1.25,
                                            minHeight: 72,
                                            display: "flex",
                                            alignItems: "center",
                                        })}
                                    >
                                        <Typography variant="body2" color="text.secondary">
                                            Restricted Visibility to Buyers
                                        </Typography>
                                    </Box>
                                </Grid>

                                {/* prawa 50% */}
                                <Grid item xs={12} md={6}>
                                    <Box
                                        sx={(t) => ({
                                            border: `1px solid ${t.palette.grey[200]}`,
                                            bgcolor: t.palette.grey[50],
                                            borderRadius: 1,
                                            px: 1.5,
                                            py: 1.25,
                                            minHeight: 72,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "flex-start",
                                            gap: 1,
                                        })}
                                    >
                                        <Chip
                                            label={restrictVisibilityToBuyers ? "ACTIVE" : "DISABLED"}
                                            color={restrictVisibilityToBuyers ? "success" : "default"}
                                            variant={restrictVisibilityToBuyers ? "filled" : "outlined"}
                                            size="small"
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>


                        {/* === Sekcja 2: Zgody === */}
                        <SectionHeader icon={<FactCheckIcon fontSize="small" />} title="Consents" />

                        {(() => {
                            type ConsentName = Exclude<keyof typeof CompanyConsentTypeEnum, number>;

                            const CONSENT_DEFS: Array<{
                                key: ConsentName;
                                label: string;
                                description: string;
                                required: boolean;
                            }> = [
                                    { key: "TermsOfService", label: "Terms of Service", description: "Acceptance is required to publish your company profile in the marketplace.", required: true },
                                    { key: "PrivacyPolicy", label: "Privacy Policy", description: "Confirmation that you have read how we process your data.", required: true },
                                    { key: "GdprDataProcessing", label: "GDPR Data Processing", description: "Consent to processing personal data in accordance with GDPR.", required: true },
                                    { key: "PublicCompanyProfile", label: "Public Company Profile", description: "Allow basic company information to be publicly visible in the marketplace.", required: true },
                                    { key: "BrandAssetsLicense", label: "Brand Assets License", description: "Allow use of your company name and logo in listings and promotional materials.", required: false },
                                    { key: "MarketingCommunications", label: "Marketing Communications", description: "Receive news and offers (optional).", required: false },
                                ];

                            const normalizeTypeName = (t: unknown): ConsentName | undefined => {
                                if (typeof t === "number") {
                                    const name = (CompanyConsentTypeEnum as any)[t];
                                    return typeof name === "string" ? (name as ConsentName) : undefined;
                                }
                                if (typeof t === "string") return t as ConsentName;
                                return undefined;
                            };

                            // Uwaga: w Twoich danych jest chyba pole "type" (lowercase)
                            const byName = (system.consents ?? []).reduce((acc, c: any) => {
                                const name = normalizeTypeName(c?.type);
                                if (name) acc[name] = c;
                                return acc;
                            }, {} as Record<ConsentName, any>);

                            const missingRequired = CONSENT_DEFS
                                .filter(d => d.required && !(byName[d.key]?.granted === true))
                                .map(d => d.label);

                            return (
                                <>
                                    {marketActiveView && missingRequired.length > 0 && (
                                        <Alert severity="error" sx={{ mb: 2 }}>
                                            Brakuje wymaganych zgód: <strong>{missingRequired.join(", ")}</strong>.
                                        </Alert>
                                    )}

                                    <Paper variant="outlined" sx={{ p: 2 }}>
                                        {CONSENT_DEFS.map(({ key, label, description, required }, idx) => {
                                            const c = byName[key];
                                            const granted = c?.granted === true;
                                            const lastChangedAt =
                                                c?.lastChangedAt ? new Date(c.lastChangedAt).toLocaleString() : "—";
                                            const lastChangedBy = c?.lastChangedBy || "—";

                                            return (
                                                <Grid
                                                    key={key}
                                                    container
                                                    spacing={1.5}
                                                    alignItems="stretch"
                                                    sx={{
                                                        py: 1.25,
                                                        borderBottom: (t) => idx < CONSENT_DEFS.length - 1 ? `1px dashed ${t.palette.divider}` : "none",
                                                    }}
                                                >
                                                    {/* Lewa połowa: opis zgody */}
                                                    <Grid item xs={12} md={6}>
                                                        <Box
                                                            sx={(t) => ({
                                                                bgcolor: t.palette.grey[100],
                                                                border: `1px solid ${t.palette.grey[200]}`,
                                                                borderRadius: 1,
                                                                px: 1.25,
                                                                py: 1.25,
                                                                minHeight: 72,
                                                                display: "flex",
                                                                flexDirection: "column",
                                                                justifyContent: "center",
                                                            })}
                                                        >
                                                            <Typography variant="body2" color="text.secondary">{label}</Typography>
                                                            <Typography variant="caption" color="text.secondary">{description}</Typography>
                                                            {required && (
                                                                <Chip size="small" label="wymagane" sx={{ mt: 0.75, width: "fit-content" }} />
                                                            )}
                                                        </Box>
                                                    </Grid>

                                                    {/* Prawa połowa: status + kiedy + kto */}
                                                    <Grid item xs={12} md={6}>
                                                        <Box
                                                            sx={(t) => ({
                                                                border: `1px solid ${t.palette.grey[200]}`,
                                                                bgcolor: t.palette.grey[50],
                                                                borderRadius: 1,
                                                                px: 1.5,
                                                                py: 1.25,
                                                                minHeight: 72,
                                                                display: "flex",
                                                                flexDirection: "column",
                                                                justifyContent: "center",
                                                                gap: 0.5,
                                                            })}
                                                        >
                                                            <Chip
                                                                size="small"
                                                                label={granted ? "granted" : "rejected"}
                                                                color={granted ? "success" : "default"}
                                                                variant={granted ? "filled" : "outlined"}
                                                                sx={{ width: "fit-content" }}
                                                            />
                                                            <Typography variant="caption" color="text.secondary">
                                                                Last changed at: {lastChangedAt}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                Changed by: {lastChangedBy}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                </Grid>
                                            );
                                        })}
                                    </Paper>
                                </>
                            );
                        })()}

                        {!marketActiveView && (
                            <Alert severity="info" sx={{ mt: 2 }}>
                                <Localized id="companies-consents-warning">_consents</Localized>
                            </Alert>
                        )}
                    </TabPanel>

                    <TabPanel value={"devices"} sx={{ p: 0, pt: 2 }}>
                        <SupplierDevicesList />
                    </TabPanel>

                </TabContext>
            </Stack>

            {/* ===== WIZARD ===== */}
            <CompanyProfileEditWizard
                open={wizardOpen}
                value={system}
                onClose={() => setWizardOpen(false)}
                onSuccessfulSave={(updated) => {
                    onUpdated(updated as CompanyProfile);
                    setWizardOpen(false);
                }}
            />
            {editField && (
                <EditCompanyProfileField
                    companyId={system.id}
                    companyProfile={system}
                    field={editField}
                    value={editValue}
                    onSave={(updated) => {
                        onUpdated(updated as CompanyProfile);
                        closeEdit();
                    }}
                    onCancel={closeEdit}
                />
            )}
        </Paper >
    );
};

const SectionHeader = ({ icon, title }: { icon: React.ReactNode; title: string }) => (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Box sx={{ display: "inline-flex", alignItems: "center" }}>{icon}</Box>
        <Typography variant="h6">{title}</Typography>
    </Stack>
);

const SectionSubHeader = ({ title }: { title: string }) => (
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {title}
    </Typography>
);
